import { motion } from "framer-motion";
import { X, Volume2, Move } from "lucide-react";
import { Button } from "./ui/button";

const EnergizerOverlay = ({ signal, groupNumber, onClose }) => {
  const isMovement = !!signal.instruction;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="energizer-overlay"
      data-testid="energizer-overlay"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        data-testid="close-energizer-btn"
        onClick={onClose}
        className="absolute top-6 right-6 p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      {/* Pulsing circles background */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.1, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#E91E8C] to-[#8B5CF6]"
        />
        <motion.div
          animate={{
            scale: [1, 1.8, 1],
            opacity: [0.2, 0.05, 0.2],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5,
          }}
          className="absolute w-[800px] h-[800px] rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#E91E8C]"
        />
      </div>

      {/* Content */}
      <motion.div
        initial={{ scale: 0.8, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
        className="relative z-10 text-center px-6"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Group Badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <span className="text-[#E2D9F3] text-lg">You are</span>
          <div className="group-badge text-3xl mt-2" data-testid="energizer-group-badge">
            Group {groupNumber}
          </div>
        </motion.div>

        {/* Signal Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.4, type: "spring", stiffness: 300 }}
          className="mb-6"
        >
          <div className="w-32 h-32 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mx-auto energizer-pulse">
            <span className="text-7xl" data-testid="energizer-icon">{signal.icon}</span>
          </div>
        </motion.div>

        {/* Signal Type */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mb-4"
        >
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
            isMovement ? "bg-[#8B5CF6]/30" : "bg-[#E91E8C]/30"
          }`}>
            {isMovement ? (
              <Move className="w-5 h-5 text-[#8B5CF6]" />
            ) : (
              <Volume2 className="w-5 h-5 text-[#E91E8C]" />
            )}
            <span className="text-white font-medium">
              {isMovement ? "Movement Signal" : "Sound Signal"}
            </span>
          </div>
        </motion.div>

        {/* Signal Name */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-4xl md:text-5xl font-bold gradient-text mb-4"
          data-testid="energizer-signal-name"
        >
          {signal.name}
        </motion.h2>

        {/* Instruction */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="text-xl text-[#E2D9F3] mb-8 max-w-md mx-auto"
          data-testid="energizer-instruction"
        >
          {isMovement ? signal.instruction : `Make this sound to find your group!`}
        </motion.p>

        {/* Alternative */}
        {signal.alternative && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="glass-light rounded-xl p-4 max-w-sm mx-auto"
          >
            <p className="text-sm text-[#E2D9F3]">
              <span className="text-[#CCFF00] font-medium">Quiet alternative: </span>
              {signal.alternative}
            </p>
          </motion.div>
        )}

        {/* Close hint */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="text-sm text-[#6B5E7C] mt-8"
        >
          Tap anywhere or press X to continue
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export default EnergizerOverlay;
